import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Tag as TagIcon } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import SEO from "@/components/SEO";
import api from "@/lib/api";

const CATEGORIES = ["All", "Insights", "Announcements", "Case Studies", "Research"];

export default function Blog() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "All";
  const tag = params.get("tag") || "";
  const page = parseInt(params.get("page") || "1", 10);

  const [data, setData] = useState({ posts: [], total: 0, total_pages: 1 });
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams({ page: String(page), per_page: "12" });
    if (category && category !== "All") q.set("category", category);
    if (tag) q.set("tag", tag);
    api.get(`/blog?${q.toString()}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
    api.get("/blog/tags").then((r) => setTags(r.data));
  }, [category, tag, page]);

  const setCategory = (c) => {
    const next = new URLSearchParams(params);
    if (c === "All") next.delete("category"); else next.set("category", c);
    next.delete("page");
    setParams(next);
  };

  return (
    <SiteLayout branch="main">
      <SEO title="Insights — Catalyst BH Consulting"
           description="Frameworks and field lessons on running effective behavioral-health programs." />

      <section className="section">
        <div className="max-w-3xl">
          <span className="eyebrow-dark">INSIGHTS</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--navy)] mt-3">
            Field notes from behavioral-health operations.
          </h1>
          <p className="text-[var(--muted)] text-base mt-5 max-w-2xl">
            Frameworks, case notes, and playbook excerpts — for clinicians, operators,
            and administrators building the next generation of care.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap gap-2" data-testid="blog-category-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-4 py-2 text-xs uppercase tracking-widest border min-h-[38px] inline-flex items-center ${
                (category === c || (c === "All" && !params.get("category")))
                  ? "bg-[var(--navy)] text-white border-[var(--navy)]"
                  : "bg-white text-[var(--navy)] border-[var(--line)] hover:border-[var(--navy)]"
              }`}
              data-testid={`blog-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {c}
            </button>
          ))}
        </div>

        {tag && (
          <p className="mt-4 text-sm text-[var(--muted)] flex items-center gap-2">
            <TagIcon size={13} /> Filtering by tag: <strong className="text-[var(--navy)]">{tag}</strong>
            <Link to="/blog" className="underline">clear</Link>
          </p>
        )}

        {loading ? (
          <p className="mt-16 text-[var(--muted)]">Loading…</p>
        ) : data.posts.length === 0 ? (
          <p className="mt-16 text-[var(--muted)]">No posts yet — check back soon.</p>
        ) : (
          <div className="grid gap-8 mt-12 md:grid-cols-2 lg:grid-cols-3" data-testid="blog-posts-grid">
            {data.posts.map((p) => <PostCard key={p.id} post={p} />)}
          </div>
        )}

        {data.total_pages > 1 && (
          <div className="mt-12 flex items-center justify-center gap-2">
            {Array.from({ length: data.total_pages }).map((_, i) => {
              const n = i + 1;
              const q = new URLSearchParams(params); q.set("page", String(n));
              return (
                <Link key={n} to={`?${q.toString()}`}
                  className={`w-9 h-9 grid place-items-center border text-sm ${
                    n === page ? "bg-[var(--navy)] text-white border-[var(--navy)]" : "border-[var(--line)] text-[var(--navy)]"
                  }`}>{n}</Link>
              );
            })}
          </div>
        )}

        {tags.length > 0 && (
          <aside className="mt-16 pt-10 border-t border-[var(--line)]">
            <h3 className="text-xs uppercase tracking-widest text-[var(--muted)] mb-4">Explore by tag</h3>
            <div className="flex flex-wrap gap-2">
              {tags.map(({ tag: t, count }) => (
                <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`}
                  className="text-xs px-3 py-1.5 min-h-[34px] inline-flex items-center border border-[var(--line)] hover:border-[var(--navy)] text-[var(--navy)]"
                  data-testid={`blog-tag-${t}`}>
                  {t} <span className="text-[var(--muted)]">· {count}</span>
                </Link>
              ))}
            </div>
          </aside>
        )}
      </section>
    </SiteLayout>
  );
}

function PostCard({ post }) {
  const date = post.published_at ? new Date(post.published_at).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  }) : "";
  return (
    <Link to={`/blog/${post.slug}`} className="group block bg-white border border-[var(--line)] hover:border-[var(--navy)] transition-colors"
          data-testid={`blog-card-${post.slug}`}>
      {post.hero_image_url && (
        <div className="aspect-[4/3] overflow-hidden bg-[var(--cream)]">
          <img src={post.hero_image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        </div>
      )}
      <div className="p-6">
        <p className="text-[10px] uppercase tracking-widest text-[var(--gold)] mb-2">
          {post.category || "Insights"} · {date}
        </p>
        <h2 className="font-display text-xl text-[var(--navy)] leading-tight">{post.title}</h2>
        {post.excerpt && (
          <p className="text-sm text-[var(--muted)] mt-3 leading-relaxed line-clamp-3">{post.excerpt}</p>
        )}
        <span className="inline-flex items-center gap-1 text-xs text-[var(--navy)] mt-4 font-medium">
          Read <ArrowRight size={12} />
        </span>
      </div>
    </Link>
  );
}
